import { Check, Circle } from "lucide-react";

import styles from "./AuthLayout.module.css";

export type StrengthLevel = "empty" | "weak" | "okay" | "good" | "strong";

interface Score {
  level: StrengthLevel;
  filled: number; // 0..4 — bars to render in the active color
  /** Human label for the level (lowercase). */
  label: string;
}

/**
 * Lightweight password strength heuristic — mirrors the spec in the rebuild
 * brief (length-driven 4-step scale with character-mix bonus). This is UX-only;
 * the backend enforces the actual policy (min 12 chars + letter + digit, see
 * `ErrWeakPassword` in `internal/admin/auth`).
 */
export function scorePassword(pw: string): Score {
  if (pw.length === 0) return { level: "empty", filled: 0, label: "—" };
  const hasLetter = /[a-zA-Z]/.test(pw);
  const hasDigit = /[0-9]/.test(pw);
  const hasSymbol = /[^A-Za-z0-9]/.test(pw);
  if (pw.length >= 16 || (hasLetter && hasDigit && hasSymbol)) {
    return { level: "strong", filled: 4, label: "strong" };
  }
  if (pw.length >= 12) return { level: "good", filled: 3, label: "good" };
  if (pw.length >= 8) return { level: "okay", filled: 2, label: "okay" };
  return { level: "weak", filled: 1, label: "weak" };
}

export interface RequirementStatus {
  longEnough: boolean;
  hasLetterAndDigit: boolean;
  notCommon: boolean;
  hasSymbol: boolean;
}

export function checkRequirements(pw: string): RequirementStatus {
  return {
    longEnough: pw.length >= 12,
    hasLetterAndDigit: /[a-zA-Z]/.test(pw) && /[0-9]/.test(pw),
    // v1: skip an actual zxcvbn-style top-1000 check; surface as always-met
    // when something is typed, so the row reads as the spec intends.
    notCommon: pw.length === 0 ? false : true,
    hasSymbol: /[^A-Za-z0-9]/.test(pw),
  };
}

interface MeterProps {
  password: string;
  /** When false, hide the requirements checklist — useful for compact rows. */
  showRequirements?: boolean;
}

export function PasswordStrengthMeter({
  password,
  showRequirements = true,
}: MeterProps) {
  const score = scorePassword(password);
  const reqs = checkRequirements(password);

  return (
    <div>
      <div
        className={styles.strength}
        aria-hidden
        data-testid="strength-meter"
        data-level={score.level}
      >
        {[0, 1, 2, 3].map((i) => {
          const fill = i < score.filled ? styles[score.level] : "";
          return (
            <span key={i} className={`${styles.bar} ${fill}`.trim()} />
          );
        })}
      </div>
      <div className={styles.strengthLabel}>
        Strength: <span className={`${styles.lvl} ${styles[score.level]}`}>{score.label}</span>
      </div>
      {showRequirements && (
        <ul className={styles.reqs}>
          <ReqRow ok={reqs.longEnough}>At least 12 characters</ReqRow>
          <ReqRow ok={reqs.hasLetterAndDigit}>One letter and one digit</ReqRow>
          <ReqRow ok={reqs.notCommon}>Not a common password</ReqRow>
          <ReqRow ok={reqs.hasSymbol}>Optional: symbol</ReqRow>
        </ul>
      )}
    </div>
  );
}

function ReqRow({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <li className={ok ? styles.met : undefined}>
      <span className={styles.icon} aria-hidden>
        {ok ? <Check size={12} /> : <Circle size={12} />}
      </span>
      {children}
    </li>
  );
}
